import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LockSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchSelect } from "@/components/SearchSelect";
import { api, formatApiErrorDetail } from "@/lib/api";

const pieceLabel = (p) =>
  [p.serial_number && `SN ${p.serial_number}`, p.lot_number && `Lot ${p.lot_number}`]
    .filter(Boolean)
    .join(" · ");

// Puts one slab piece on hold for a customer: opens a reservation and marks the piece Reserved.
export const PieceHoldDialog = ({ slab, piece, open, onClose, onSlab }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [project, setProject] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomer("");
    setProject(`Hold · ${slab.slab_color || ""}`.trim());
    setNote("");
    api
      .get("/customers")
      .then(({ data }) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => setCustomers([]));
  }, [open, slab.slab_color]);

  const options = customers.map((c) => ({
    value: c.id,
    label: c.company ? `${c.name} — ${c.company}` : c.name,
    hint: c.group && c.group !== "Regular" ? c.group : "",
  }));
  const picked = customers.find((c) => c.id === customer);
  const customerName = picked ? picked.name : customer;

  const submit = async () => {
    if (!customerName.trim()) {
      toast.error("Pick or type a customer");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/slabs/${slab.id}/pieces/${piece.id}/hold`,
        {
          customer_name: customerName.trim(),
          customer_id: picked ? picked.id : null,
          project_name: project,
          comments: note,
        },
      );
      onSlab(data.slab);
      toast.success(`On hold for ${customerName} · ${data.reservation.tag}`, {
        action: {
          label: "Open hold",
          onClick: () => navigate(`/reservation/${data.reservation.id}`),
        },
      });
      onClose();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not put this piece on hold",
      );
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="piece-hold-dialog"
        className="max-w-md border-zinc-800 bg-zinc-900"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockSimple size={18} weight="bold" className="text-amber-400" />
            Hold this piece
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {slab.slab_color}
            {piece && pieceLabel(piece) ? ` · ${pieceLabel(piece)}` : ""} — a
            reservation opens for the customer and the piece leaves the public
            floor until it is released.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Customer
            </Label>
            <SearchSelect
              testId="piece-hold-customer"
              value={customer}
              options={options}
              onChange={setCustomer}
              allowCustom
              placeholder="Search accounts or type a name…"
              searchPlaceholder="Search customers…"
              emptyText="No account — type the name to use it"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Project
            </Label>
            <Input
              data-testid="piece-hold-project"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="h-11 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Note (optional)
            </Label>
            <Textarea
              data-testid="piece-hold-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Deposit taken, template date, who asked…"
              className="border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              data-testid="piece-hold-cancel"
              variant="outline"
              onClick={onClose}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="piece-hold-submit"
              disabled={busy}
              onClick={submit}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <LockSimple size={14} weight="bold" className="mr-1.5" />
              {busy ? "Holding…" : "Put on hold"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
